import { CommerceLayer } from '@commercelayer/sdk/bundle'
import { jwtVerify } from '@commercelayer/js-auth'
import { useStorage } from '@vueuse/core'
import * as clUtils from '@commercelayer/sdk-utils'
import { supabase } from '@/supabase'
import * as Sentry from '@sentry/vue'
import { fetchEdgeFunctionError } from '@/utils/supabase'

class CommercelayerError extends Error {
  constructor(message) {
    super(message)
    this.name = 'CommercelayerError'
  }
}

const TRIGGER_TOKEN_REFRESH_TIME = 60 * 60 * 1000 // 60 minutes - token is valid for 12h

export const commerceTokenStorage = useStorage('commercelayer-token', null, sessionStorage)

export function clearCommerceToken() {
  commerceTokenStorage.value = null
}

// Prevent multiple simultaneous token refresh requests
let tokenRefreshPromise = null

async function handleAuthFailure() {
  alert(
    'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an. - Tritt der Fehler weiterhin auf, wende Dich bitte an den IT-Support.'
  )
  clearCommerceToken()
  await supabase.auth.signOut()
}

async function verifyInStorageToken() {
  if (!commerceTokenStorage.value) {
    return null
  }
  try {
    const decodedToken = await jwtVerify(commerceTokenStorage.value)
    const expirationTime = decodedToken.payload.exp * 1000
    const currentTime = Date.now()

    // fetch new token if the current one expires in less than 10 minutes
    if (expirationTime - currentTime < TRIGGER_TOKEN_REFRESH_TIME) {
      // Clear storage for soon-to-expire token
      clearCommerceToken()
      return null
    }

    // if it's not an integration token, return null -> only store integration tokens
    if (decodedToken.payload.application.kind !== 'integration') {
      clearCommerceToken()
      return null
    }
    return commerceTokenStorage.value
  } catch (error) {
    clearCommerceToken()
    return null
  }
}

// Requests a brand-new integration token from the edge function and resolves to the access
// token STRING (or throws). Kept separate from getCommerceToken so the in-flight refresh can be
// memoised as a promise-of-token — see the race note in getCommerceToken.
// Will throw an error if the user (employee table) has no live role assigned.
// If no supabase session exists, it will return as sales token -> for public access;
// only an employee with a valid session can get an integration token.
async function requestFreshCommerceToken(supabase, shouldHandleAuthFailure = false) {
  const { data, error } = await supabase.functions.invoke('auth-pos-commercelayer-jwt', {
    body: JSON.stringify({
      env: import.meta.env.VITE_WEB_ENV
    }),
    region: 'eu-central-1'
  })

  if (data?.accessToken) {
    try {
      const decodedToken = await jwtVerify(data.accessToken)
      if (decodedToken.payload.application.kind === 'integration') {
        commerceTokenStorage.value = data.accessToken
      } else {
        clearCommerceToken()
      }
    } catch (e) {
      Sentry.captureException(e, (scope) => {
        scope.setTransactionName('tokenDecodeError')
        return scope
      })
    }

    return data.accessToken
  }

  // Handle authentication failures
  if (error?.context?.status === 401 && shouldHandleAuthFailure) {
    const session = await supabase.auth.getSession()

    // if we have an active session, refresh the token
    if (session?.data?.session?.refresh_token) {
      const refreshedSession = await supabase.auth.refreshSession({
        refresh_token: session?.data?.session?.refresh_token
      })

      // try to get the token again with the refreshed session (no further auth-failure retry)
      if (refreshedSession?.data?.session?.access_token) {
        clearCommerceToken()
        return await requestFreshCommerceToken(supabase, false)
      }
    }

    // logout the user if the token is invalid and can't be refreshed
    await handleAuthFailure()
    throw new CommercelayerError(
      'Authentication failed and could not refresh token. User has been signed out.'
    )
  }

  const errorMessage = await fetchEdgeFunctionError(error)
  Sentry.captureException(new Error('Failed to get commerce token'), (scope) => {
    scope.setContext('errorDetails', {
      requestStatus: error?.context?.status,
      edgeFunctionData: data,
      edgeFunctionErrorMessage: errorMessage,
      status: error?.context?.status,
      shouldHandleAuthFailure
    })
    return scope
  })

  clearCommerceToken()

  // Throw error instead of returning undefined
  throw new CommercelayerError(
    `Failed to fetch CommerceLayer token: ${errorMessage?.message || 'Unknown error'}`
  )
}

// Exported for unit testing (concurrent-refresh regression, WINGS-4MN / WINGS-5QM).
export async function getCommerceToken(supabase, shouldHandleAuthFailure = false, forceNewToken = false) {
  // Only check storage if not forcing a new token
  if (!forceNewToken) {
    const tokenInStorage = await verifyInStorageToken()
    if (tokenInStorage) {
      return tokenInStorage
    }
  } else {
    clearCommerceToken()
  }

  // Prevent multiple simultaneous token refresh requests. The memoised promise MUST resolve to
  // the access token STRING: it previously held the raw invoke() result, so a second concurrent
  // caller received the whole { data, error } object as its "token", sent it as
  // `Bearer [object Object]`, and Commerce Layer rejected it with INVALID_TOKEN. This is the main
  // source of the WINGS-4MN / WINGS-5QM INVALID_TOKEN errors on long-lived POS sessions, where
  // many requests 401 at once when the token expires and all refresh in parallel.
  if (tokenRefreshPromise) {
    return tokenRefreshPromise
  }

  tokenRefreshPromise = requestFreshCommerceToken(supabase, shouldHandleAuthFailure).finally(() => {
    tokenRefreshPromise = null
  })

  return tokenRefreshPromise
}

async function init() {
  try {
    const token = await getCommerceToken(supabase)

    // getCommerceToken throws on error, but check for safety
    if (!token) {
      throw new CommercelayerError('Failed to initialize: No token available')
    }

    const cl = CommerceLayer({
      organization: import.meta.env.VITE_CL_ORG,
      accessToken: token
    })

    const onInvalidToken = async (input, init) => {
      try {
        const token = await getCommerceToken(supabase, true, true)

        if (!token) {
          throw new CommercelayerError('Failed to refresh token on 401')
        }

        cl.config({ accessToken: token })
        init.headers['Authorization'] = `Bearer ${token}`
        return await fetch(input, init)
      } catch (error) {
        await handleAuthFailure()
        Sentry.captureException(error, (scope) => {
          scope.setTransactionName('tokenRefreshOn401Failed')
          return scope
        })
        throw error
      }
    }

    // Fetch interceptor to handle 401 errors and refresh the token automatically and retry the request
    const customFetch = async (input, init) => {
      const res = await fetch(input, init)

      if (res.status === 401) {
        return await onInvalidToken(input, init)
      }
      return res
    }

    cl.config({ fetch: customFetch })

    cl.addResponseInterceptor(
      (successResponse) => successResponse,
      (errorResponse) => {
        if (errorResponse?.errors?.length > 0) {
          const errorApi = errorResponse.errors[0]
          Sentry.setContext('commercelayer-sdk-errors', {
            errors: errorResponse.errors
          })
          // convert the error to a CommercelayerError to have a consistent error handling
          return new CommercelayerError(`${errorApi.code}: ${errorApi.detail}`)
        } else {
          return errorResponse
        }
      }
    )

    return cl
  } catch (e) {
    Sentry.captureException(e)
    throw new Error(
      `Error fetching CommerceLayer TOKEN: ${import.meta.env.VITE_WEB_ENV.toUpperCase()}`
    )
  }
}

export default async function () {
  const cl = await init()
  clUtils.CommerceLayerUtils(cl)
  return { cl, clUtils }
}
